import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AgGridReact } from "ag-grid-react";
import {
  ColumnResizedEvent,
  FilterChangedEvent,
  FilterModifiedEvent,
  GridApi,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { constants } from "../../common/constants";
import { getRowStyle } from "../../common/commonFunctions";
import { onGridSizeChanged } from "../AgGrid/grid-util";

interface AgGridProps {
  title?: string;
  rowData: any;
  height?: any;
  type?: string;
  onCellChange?: any;
  getBtnRef?: any;
  getGridApi?: any;
  isDeselectAll?: boolean;
  getContextMenuItems?: any;
  globalSearch?: any;
  defaultFilter?: any;
  columnDefinitions: any;
  isTotalRowHide: boolean;
  onRowSelect?:any;
}
const CommonAgGrid = ({
  rowData,
  height,
  getBtnRef,
  getGridApi,
  isDeselectAll,
  getContextMenuItems,
  globalSearch,
  defaultFilter,
  columnDefinitions,
  isTotalRowHide,
  onRowSelect
}: 
AgGridProps) => {
  const gridRef = useRef<any>(null);
  const [columnDefs] = useState<any>([...columnDefinitions]);

  let gridApi: any;
  const defaultColDef = useMemo<any>(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
      floatingFilter: false,
      suppressFilterButton: false,
      editable: true,
      resizable: true,
      headerCheckboxSelectionFilteredOnly: true,
      tooltipComponent: CustomTooltip,
      menuTabs: [constants.FilterMenuTab],
      tooltipShowDelay: 10,
      tooltipMouseTrack: true,
      suppressKeyboardEvent: (params: any) => {
        let isTabKey = params?.event?.key === constants.Tab;
        if (isTabKey) gridRef?.current?.api?.stopEditing();
        return false;
      },
    };
  }, []);

  useEffect(() => {
    gridApi = gridRef.current!.api;

    var gridColumnApi = gridRef.current!.columnApi;
    getUpdatedData(rowData);
    if (gridApi && gridColumnApi) getName(gridApi, gridColumnApi);
    getBtnRef(gridRef);
  }, [rowData]);
  const onColumnResized = useCallback((params: ColumnResizedEvent) => {}, []);

  const getUpdatedData = useCallback(
    async (data: any) => {
      await gridApi?.applyTransactionAsync({ update: [rowData] });
      gridApi?.refreshCells();
    },
    [rowData]
  );

  useEffect(() => {
    if (defaultFilter && gridRef) {
      const filterComponent = gridRef?.current?.api?.getFilterInstance(
        defaultFilter.column
      );
      if (filterComponent) {
        filterComponent?.setModel({ values: defaultFilter.values });
        gridApi.onFilterChanged();
      }
    }
  }, [gridRef]);

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit();
    gridApi = params.api;

    var gridColumnApi = params.columnApi;
    getName(gridApi, gridColumnApi);
    // Emmits the gridApi refrence to the parent component
    if (getGridApi) {
      getGridApi(gridApi, gridColumnApi);
    }
  };

  /**
   * Executes whenever Grid/Table filter modifies - this is more sensitive
   * @param params
   */
  const onFilterModified = (params: FilterModifiedEvent) => {
    const { api, columnApi } = params;
  };

  /**
   * Executes whenever Grid/Table filter changes
   * @param params
   */
  const onFilterChanged = (params: FilterChangedEvent) => {
    const { api, columnApi } = params;
    api.deselectAll();
    getName(api, columnApi);
  };

  /**
   * To skip pinned rows on the basis of Page path
   * @returns shouldSkip : boolean
   */
  const skipPinnedRow = (): boolean => {
    return false;
  };

  const getName = (api: any, columnApi: any) => {
    if (!api || !columnApi || skipPinnedRow()) return;
    let rows: any = {};
    var result = [];
    columnApi?.getAllGridColumns().forEach((item: any) => {
      rows[item.colId] = null;
    });
    const columnsWithSumAggregation = ['qty']
    const columnsWithAvgAggregation = ['rate']
    columnsWithSumAggregation.forEach(element => {
      api?.forEachNodeAfterFilter((rowNode: any) => {
        if (rowNode && rowNode.data && rowNode.data[element]) {
          rows[element] += Number(parseFloat(rowNode.data[element]))
        }
      })
    })

    columnsWithAvgAggregation.forEach(element => {
      const nodeCount = api.getDisplayedRowCount()
      const avg = (rows[element] / nodeCount).toString()
      rows[element] = nodeCount !== 0 ? Number(parseFloat(avg).toFixed(2)) : 0
    })

    if (!isTotalRowHide) {
      result.push({
        isTotalRow: true,
        filled: "Total Date Count",
        sec_id: "Total Ticker",
        pnl: "Total PNL",
        client: "Total Client",
        locate_count: "Total Locate Count",
      });
    }

    rows["isTotalRow"] = true;
   
    rows['lender'] = countUnique('lender', api)
    rows['ticker'] = countUnique('ticker', api)
    result.push(rows);

    api?.setPinnedBottomRowData(result);
  };

  /**
   * Method counts the unique value for given attribute (for filtered values also)
   * @param attribute
   * @returns unique values count
   */
  const countUnique = (attribute: string, api: GridApi): number => {
    const uniqueItemsSet: Set<any> = new Set();
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (rowNode?.data instanceof Object && attribute in rowNode.data) {
        uniqueItemsSet.add(rowNode.data[attribute]);
      }
    });
    return uniqueItemsSet.size;
  };

  useEffect(() => {
    if (isDeselectAll != undefined) gridRef.current?.api?.deselectAll();
  }, [isDeselectAll]);

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 100,
      maxWidth: 200,
    };
  }, []);

  const getRowId = (params: any) => {
    if (params && params.data) {
      if (params.data.id) {
        return params.data.id;
      }
    }
    return makeid(5);
  };

  function makeid(length: any) {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: "",
    };
  }, []);

  function isForceRefreshSelected() {
    return (document.querySelector("#forceRefresh") as HTMLInputElement)
      ?.checked;
  }
  useEffect(() => {
    let refreshParams: any = {
      force: true,
      suppressFlash: true,
    };
    gridRef?.current!.api?.refreshCells(refreshParams);
  }, [globalSearch]);

  function isSuppressFlashSelected() {
    return (document.querySelector("#suppressFlash") as HTMLInputElement)
      ?.checked;
  }
  const onGridRowDataChanged = (params: any) => {
    let refreshParams: any = {
      force: isForceRefreshSelected(),
      suppressFlash: isSuppressFlashSelected(),
    };
    gridRef.current!.api.refreshCells(refreshParams);
  };

  const onRowClicked = (params: any) => {
    if (params?.data?.ticker) {
      onRowSelect(params?.data);
    }
  };

  return (
    <div
      className="ag-theme-balham"
      style={{
        width: "100%",
        height:'97%'
      }}
    >
      <AgGridReact
        ref={gridRef}
        rowStyle={{
          justifyContent: "center",
          color: "#2D2D2D",
          fontSize: "0.75rem",
          fontWeight: "400",
          fontStyle: "normal",
        }}
        rowHeight={22}
        columnDefs={columnDefs}
        getRowId={getRowId}
        onRowDataUpdated={onGridRowDataChanged}
        rowData={rowData}
        suppressRowClickSelection={false}
        defaultColDef={defaultColDef}
        suppressAggFuncInHeader={true}
        enableCellChangeFlash={false}
        enableRangeSelection={false}
        onGridReady={onGridReady}
        onFilterModified={onFilterModified}
        onFilterChanged={onFilterChanged}
        onGridSizeChanged={onGridSizeChanged}
        onColumnResized={onColumnResized}
        rowSelection={"multiple"}
        autoGroupColumnDef={autoGroupColumnDef}
        groupSelectsChildren={true}
        groupSelectsFiltered={true}
        sideBar={sideBar}
        pivotMode={false}
        cacheQuickFilter={false}
        getContextMenuItems={getContextMenuItems}
        quickFilterText={globalSearch}
        overlayLoadingTemplate={
          '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
        }
        // rowGroupPanelShow="always"
        getRowStyle={getRowStyle}
        onRowClicked={onRowClicked}
      ></AgGridReact>
    </div>
  );
};

function CustomTooltip() {}
CustomTooltip.prototype.init = function (params: any) {
  var type = params.type || "primary";
  var data = params.api.getDisplayedRowAtIndex(params.rowIndex).data;
  var eGui = (this.eGui = document.createElement("div"));
  eGui.classList.add("custom-tooltip");
  this.eGui.innerHTML =
    '<div class="panel panel-' +
    type +
    '">' +
    '<div class="panel-heading">' +
    '<h3 class="panel-title">' +
    data.country +
    "</h3>" +
    "</div>" +
    '<div class="panel-body">' +
    '<h4 style="white-space: nowrap;">' +
    data.athlete +
    "</h4>" +
    "<p>Total: " +
    data.total +
    "</p>" +
    "</div>" +
    "</div>";
};
CustomTooltip.prototype.getGui = function () {
  return this.eGui;
};
export default CommonAgGrid;
